import { ApiError, getMessage, request } from './client';
import { scanDetailSchema, scanListSchema, scanSchema } from '../components/scans/scanSchema';

/**
 * Admin receipt scans: review queue, edit, approve, reject. Always hits the real backend.
 * Envelope: { Success, Message, Data }. Errors carry Data.code (SCAN_NOT_PENDING, MISSING_FIELDS,
 * DUPLICATE_RECEIPT) or Data.errors. Types: see components/scans/scanSchema.js.
 */

const unexpected = () => new ApiError('Unexpected response from the server.', 200, null);

function parseDetail(data) {
  const parsed = scanDetailSchema.safeParse(data);
  if (!parsed.success) throw unexpected();
  return parsed.data;
}

/** `Data.code` of a failed request, e.g. "SCAN_NOT_PENDING". */
export const errorCode = (err) => (err instanceof ApiError ? err.data?.Data?.code ?? null : null);

/**
 * GET admin/scans?status&page&limit&search&userId&placeId
 * @param {{ status: string, page: number, limit: number, search?: string, userId?: string, placeId?: string }} params
 */
export async function getScans({ status = 'pending', page, limit, search = '', userId = '', placeId = '' }, { signal } = {}) {
  const qs = new URLSearchParams({ status, page: String(page), limit: String(limit) });
  if (search) qs.set('search', search);
  if (userId) qs.set('userId', userId);
  if (placeId) qs.set('placeId', placeId);
  const res = await request(`admin/scans?${qs}`, { signal });

  const envelope = scanListSchema.safeParse(res?.Data);
  if (!envelope.success) throw unexpected();
  const scans = envelope.data.scans.flatMap((raw, index) => {
    const row = scanSchema.safeParse(raw);
    if (row.success) return [row.data];
    console.warn(`[scans] skipped malformed scan at index ${index}`);
    return [];
  });
  return { ...envelope.data, scans };
}

/** GET admin/scans/:id (404 if it doesn't exist). */
export async function getScan(id, { signal } = {}) {
  const res = await request(`admin/scans/${encodeURIComponent(id)}`, { signal });
  return parseDetail(res?.Data);
}

/** PATCH admin/scans/:id with only the changed fields ("Save Edits"; the scan stays pending). */
export async function saveScanEdits(id, changes) {
  const res = await request(`admin/scans/${encodeURIComponent(id)}`, { method: 'PATCH', body: changes });
  return { message: getMessage(res), scan: parseDetail(res?.Data) };
}

/** PUT admin/scans/:id { status: "approved", ...changes }: saves the edits and approves in one call. */
export async function approveScan(id, changes = {}) {
  const res = await request(`admin/scans/${encodeURIComponent(id)}`, { method: 'PUT', body: { ...changes, status: 'approved' } });
  return { message: getMessage(res), scan: parseDetail(res?.Data) };
}

/** PUT admin/scans/:id { status: "rejected", reason }. */
export async function rejectScan(id, reason) {
  const res = await request(`admin/scans/${encodeURIComponent(id)}`, { method: 'PUT', body: { status: 'rejected', reason } });
  return { message: getMessage(res), scan: parseDetail(res?.Data) };
}

export const scansApi = { getScans, getScan, saveScanEdits, approveScan, rejectScan };
